import React, {useRef} from 'react'
import Text from '@component/Text/Text'
import {useRouter} from 'next/router'
import {useTranslation} from '@lib/intl/useTranslations'

import {createLog} from '@util/logs'
import {showToast} from '@component/snackbar/WizToastMessage'
import useNavigation from '@hook/useNavigation'
import useCopy from '@hook/useCopy'
import useQueryGetUserSidebar from '@hook/query/portfolio/useQueryGetUserSidebar'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'

import MobileUserProfileStatusModal from '@feature/portfolio/components/MobileUserProfileStatusModal/MobileUserProfileStatusModal'
import SharePortfolioOptionModal from '@feature/portfolio/components/SharePortfolioOptionModal/SharePortfolioOptionModal'
import NotRegisteredApiModal from '@feature/portfolio/components/NotRegisteredApiModal/NotRegisteredApiModal'

import AnnounceIcon from '@svg/common/ic_announce.svg'
import ShareIcon from '@svg/common/ic_btn_share_v2.svg'
import MobileUserSubInformationSkeleton from '@feature/portfolio/components/MobileUserProfileHeader/skeletons/MobileUserSubInformationSkeleton'

interface IProps {
    className?: string
    id?: string
    login_id?: string
}

const MobileUserSubInformation = ({className, id, login_id}: IProps) => {
    const t = useTranslation()
    const router = useRouter()
    const {copyToClipboard} = useCopy()
    const {goSubscriber} = useNavigation()

    const userProfileStatusModalRef = useRef<any>()
    const sharePortfolioOptionModalRef = useRef<any>()
    const notRegisteredApiModalRef = useRef<any>()

    const {data: me} = useQueryFetchMe()
    const {data: sidebar, isLoading} = useQueryGetUserSidebar(id, false)

    const onClickFollowFollowing = () => {
        createLog('event', 'portfolio_user_profile_header_click_following')
        goSubscriber('following')
    }
    const onClickFollowFollowers = () => {
        createLog('event', 'portfolio_user_profile_header_click_followers')
        goSubscriber('followers')
    }

    const onClickOpenUserProfileStatusModal = () => {
        createLog('event', 'portfolio_user_profile_header_open_user_profile_status_modal')
        userProfileStatusModalRef.current?.show()
    }

    const onClickOpenSharePortfolioModal = () => {
        const isMyPortfolio = id === login_id
        if ((isMyPortfolio && !me?.keys) || (isMyPortfolio && me?.keys?.length < 1)) {
            notRegisteredApiModalRef?.current?.show()
            return
        }

        if (isMyPortfolio) {
            createLog('event', 'portfolio_user_profile_header_open_share_portfolio_option_modal')
            sharePortfolioOptionModalRef?.current?.show()
        } else {
            createLog('event', 'portfolio_user_profile_header_share_portfolio_copy_link')
            copyToClipboard(`${window.location.origin}/${router.locale}${router?.asPath}`)
            showToast(t('common.copied'))
            sharePortfolioOptionModalRef?.current?.close()
        }
    }

    return (
        <div
            className={`flex items-center justify-between gap-x-[10px] text-body3 text-gray02 dark:text-dark_gray02 ${className}`}>
            {isLoading ? (
                <MobileUserSubInformationSkeleton />
            ) : (
                <div className={'flex items-center gap-x-[5px]'}>
                    <button
                        disabled={id !== login_id}
                        className={
                            'flex items-center gap-x-[3px] py-[7px] px-[5px] hover:bg-gray07 hover:dark:bg-dark_gray07 rounded-[3px]'
                        }
                        onClick={onClickFollowFollowing}>
                        <Text className={'text-ti3'}>{sidebar?.following}</Text>
                        <Text>{t('portfolio.sidebar.subscribing.title')}</Text>
                    </button>
                    <button
                        disabled={id !== login_id}
                        className={
                            'flex items-center gap-x-[3px] py-[7px] px-[5px] hover:bg-gray07 hover:dark:bg-dark_gray07 rounded-[3px]'
                        }
                        onClick={onClickFollowFollowers}>
                        <Text className={'text-ti3'}>{sidebar?.followers}</Text>
                        <Text>{t('portfolio.sidebar.subscribers.title')}</Text>
                    </button>
                </div>
            )}

            <div className={'flex items-center gap-x-[5px]'}>
                <div
                    className={'flex items-center gap-x-[3px] py-[7px] px-[5px] cursor-pointer'}
                    onClick={onClickOpenUserProfileStatusModal}>
                    <AnnounceIcon className={'w-[14px] h-[14px] fill-gray02 dark:fill-dark_gray02'} />
                    <Text>{t('portfolio.user.profile.memo.title')}</Text>
                </div>
                <div
                    className={'flex items-center gap-x-[3px] py-[7px] px-[5px] cursor-pointer'}
                    onClick={onClickOpenSharePortfolioModal}>
                    <ShareIcon className={'w-[14px] h-[14px] fill-gray02 dark:fill-dark_gray02'} />
                    <Text>{t('portfolio.user.profile.share.title')}</Text>
                </div>
            </div>

            <MobileUserProfileStatusModal ref={userProfileStatusModalRef} id={id} login_id={login_id} />
            <SharePortfolioOptionModal ref={sharePortfolioOptionModalRef} />
            <NotRegisteredApiModal ref={notRegisteredApiModalRef} />
        </div>
    )
}
export default MobileUserSubInformation
